import type { Distance, SpatialRectangle } from "@pdfking/math"
import type { PageNumber } from "@pdfking/renderer-api"
import type { PdfEngine } from "../types/engine/PdfEngine"
import type { TaskResult } from "../types/tasks/input/TaskResult"
import type { TaskPlotOptions } from "../types/tasks/options/TaskPlotOptions"
import type { TrackingCallback } from "../types/tasks/tracking/TrackingCallback"
import { BlobArray } from "@pdfking/compatibility"
import { CreateCompletePage, CreateContext } from "@pdfking/renderer-api"
import { PDFPlot } from "@pdfking/renderer-default"
import { IterableToArray, RecordMap } from "@pdfking/tfunc"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { ContentType } from "../types/files/ContentType"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TaskName } from "../types/tasks/TaskName"
import { TrackedTaskName } from "../types/tasks/tracking/TrackedTaskName"

// options PlotPdfOptions - file: Plot
// stream -
// emit TrackingCallback
//
// return DocumentPage[page, GeneratedFile[]] // takes the page number from PlotPdfOptions

export async function* TaskPlot(
  task: TaskPlotOptions,
  emit: TrackingCallback,
  engine: PdfEngine
): AsyncGenerator<TaskResult> {
  const emitter = (done: number) =>
    emit({
      done,
      name: TrackedTaskName.PLOT_PDF,
      total: 1,
    })

  void emitter(0)

  const context = CreateContext({
    ...task.options,
    ...task.context,
    file_map: RecordMap(
      file =>
        RequestBlob(engine, file)
          .then(({ stream }) => IterableToArray(stream))
          .then(BlobArray),
      task.context.file_map || {}
    ),
  })
  const page = CreateCompletePage(task.page, context)

  const content = await GenerateFile(
    engine,
    PDFPlot([page], context),
    ContentType.PDF,
    emit
  )

  const bleed = page.bleed
  const width = (page.width + bleed * 2) as Distance
  const height = (page.height + bleed * 2) as Distance

  const trim_box: SpatialRectangle<Distance> = {
    height: page.height,
    width: page.width,
    x: bleed,
    y: bleed,
  }

  const media_box: SpatialRectangle<Distance> = {
    height,
    width,
    x: 0 as Distance,
    y: 0 as Distance,
  }

  yield {
    content: {
      content,
      context: task.context,
      enum: VirtualFileType.SINGLE_PAGE_PDF,
      file_meta: {},
      media_box,
      page,
      page_count: 1 as PageNumber,
      page_number: 1 as PageNumber,
      size: media_box,
      source: undefined,
      trim_box,
    },
    enum: TaskName.CONTENT_FILE,
    name: "plot.pdf",
  }

  void emitter(1)
}
